import { useState, useMemo } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  subMonths,
  addMonths,
  format,
  isToday,
  isSameMonth,
  isSameDay,
  parseISO,
} from "date-fns";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function generateCalendarDays(currentDate, selectedDate) {
  const start = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 }); // Monday
  const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });

  const days = [];
  let day = start;

  while (day <= end) {
    days.push({
      date: format(day, "yyyy-MM-dd"),
      isCurrentMonth: isSameMonth(day, currentDate),
      isToday: isToday(day),
      isSelected: selectedDate ? isSameDay(day, selectedDate) : false,
    });
    day = addDays(day, 1);
  }

  return days;
}

const Calendar = ({ onSelectDate, onMonthChange, dayCounts = [] }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const days = generateCalendarDays(currentMonth, selectedDate);
  const isViewingCurrentMonth = isSameMonth(currentMonth, new Date());

  const dayCountMap = useMemo(() => {
    const map = {};
    dayCounts.forEach(({ date, count }) => {
      map[date] = count;
    });
    return map;
  }, [dayCounts]);

  const goToPreviousMonth = () => {
    const prev = subMonths(currentMonth, 1);
    setCurrentMonth(prev);
    onMonthChange?.(prev);
  };
  const goToNextMonth = () => {
    const next = addMonths(currentMonth, 1);
    setCurrentMonth(next);
    onMonthChange?.(next);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today);
    setSelectedDate(today);
    onSelectDate(today);
    onMonthChange?.(today);
  };

  return (
    <div className="mt-10 text-center lg:col-start-8 lg:col-end-13 lg:row-start-1 lg:mt-9 xl:col-start-9">
      <div className="flex items-center text-ink-900">
        <button
          type="button"
          onClick={goToPreviousMonth}
          className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-ink-400 hover:text-ink-500"
        >
          <span className="sr-only">Previous month</span>
          <ChevronLeftIcon className="size-5" aria-hidden="true" />
        </button>
        <div className="flex-auto font-display text-base tracking-display">
          {format(currentMonth, "MMMM yyyy")}
        </div>
        {!isViewingCurrentMonth && (
          <button
            type="button"
            onClick={goToToday}
            className="mr-1.5 rounded-md bg-cream-200 px-2.5 py-1 text-xs font-medium text-ink-600 hover:bg-cream-300 transition-colors"
          >
            Today
          </button>
        )}
        <button
          type="button"
          onClick={goToNextMonth}
          className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-ink-400 hover:text-ink-500"
        >
          <span className="sr-only">Next month</span>
          <ChevronRightIcon className="size-5" aria-hidden="true" />
        </button>
      </div>
      <div className="mt-6 grid grid-cols-7 smallcaps text-ink-400">
        <div>M</div>
        <div>T</div>
        <div>W</div>
        <div>T</div>
        <div>F</div>
        <div>S</div>
        <div>S</div>
      </div>
      <div className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-cream-300 text-sm shadow ring-1 ring-ink-100">
        {days.map((day, dayIdx) => (
          <button
            key={day.date}
            type="button"
            onClick={() => {
              const selectedDate = parseISO(day.date);
              setSelectedDate(selectedDate);
              onSelectDate(selectedDate);
            }}
            className={classNames(
              "py-1.5 hover:bg-cream-200 focus:z-10",
              day.isCurrentMonth ? "bg-white" : "bg-cream-100",
              (day.isSelected || day.isToday) && "font-semibold",
              day.isSelected && "text-white",
              !day.isSelected &&
                day.isCurrentMonth &&
                !day.isToday &&
                "text-ink-900",
              !day.isSelected &&
                !day.isCurrentMonth &&
                !day.isToday &&
                "text-ink-400",
              day.isToday && !day.isSelected && "text-accent-600",
              dayIdx === 0 && "rounded-tl-lg",
              dayIdx === 6 && "rounded-tr-lg",
              dayIdx === days.length - 7 && "rounded-bl-lg",
              dayIdx === days.length - 1 && "rounded-br-lg"
            )}
          >
            <time
              dateTime={day.date}
              className={classNames(
                "mx-auto flex size-7 items-center justify-center rounded-full",
                day.isSelected && day.isToday && "bg-accent-500",
                day.isSelected && !day.isToday && "bg-ink-900"
              )}
            >
              {day.date.split("-").pop().replace(/^0/, "")}
            </time>
            {dayCountMap[day.date] > 0 && (
              <div className="flex justify-center gap-0.5 mt-0.5">
                <span className={classNames(
                  "block size-1 rounded-full",
                  dayCountMap[day.date] >= 6 ? "bg-accent-500" :dayCountMap[day.date] >= 3 ? "bg-signal-amber-500" : "bg-ink-300"
                )} />
                {dayCountMap[day.date] >= 3 && (
                  <span className={classNames(
                    "block size-1 rounded-full",
                    dayCountMap[day.date] >= 6 ? "bg-accent-500" :"bg-signal-amber-500"
                  )} />
                )}
                {dayCountMap[day.date] >= 6 && (
                  <span className="block size-1 rounded-full bg-accent-500" />
                )}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default Calendar;
